import { supabase, TABLES, OPPORTUNITY_STAGES } from '../config/supabase';
import { z } from 'zod';

/**
 * Standardized error object structure
 */
const createError = (message, code = 'UNKNOWN_ERROR', details = null) => ({
    success: false,
    message,
    code,
    details
});

/**
 * Standardized success response structure
 */
const createSuccess = (data, message = null) => ({
    success: true,
    data,
    ...(message && { message })
});

/**
 * Parses Supabase errors
 */
const parseSupabaseError = (error, operation) => {
    if (!error) {
        return createError(`Unknown error during ${operation}`, 'UNKNOWN_ERROR');
    }

    const errorMap = {
        'PGRST116': { message: 'Opportunity not found', code: 'NOT_FOUND' },
        '23505': { message: 'An opportunity with this name already exists', code: 'DUPLICATE' },
        '42501': { message: 'Permission denied', code: 'PERMISSION_DENIED' }
    };

    const mappedError = errorMap[error.code];
    if (mappedError) {
        return createError(mappedError.message, mappedError.code, { originalError: error });
    }

    return createError(
        error.message || `Failed to ${operation}`,
        error.code || 'DATABASE_ERROR',
        { originalError: error }
    );
};

// Zod Schema for Opportunity Validation
const opportunitySchema = z.object({
    name: z.string().min(1, 'Opportunity name is required'),
    amount: z.number().min(0, 'Amount must be positive').optional().nullable(),
    currency: z.string().default('USD'),
    stage: z.enum(Object.values(OPPORTUNITY_STAGES)).default(OPPORTUNITY_STAGES.LEAD),
    close_date: z.string().optional().nullable(),
    account_id: z.string().uuid('Invalid Account ID').optional().nullable(),
    primary_contact_id: z.string().uuid('Invalid Contact ID').optional().nullable(),
    owner_user_id: z.string().uuid('Invalid User ID').optional().nullable(),
    team_id: z.string().uuid('Invalid Team ID').optional().nullable(),
    probability: z.number().min(0).max(100).optional().nullable(),
    description: z.string().optional().nullable(),
    checklist_data: z.record(z.any()).optional().nullable()
});

class OpportunitiesService {
    constructor() {
        this.tableName = TABLES.OPPORTUNITIES;
    }

    async getOpportunities(options = {}) {
        try {
            const { select = '*, accounts(name), contacts(first_name, last_name), users(full_name), teams(name)', filters = {}, orderBy = 'created_at', ascending = false } = options;

            let query = supabase
                .from(this.tableName)
                .select(select);

            // Apply filters
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    query = query.eq(key, value);
                }
            });

            if (orderBy) query = query.order(orderBy, { ascending });

            const { data, error } = await query;

            if (error) return parseSupabaseError(error, 'fetch opportunities');

            return createSuccess(data || []);
        } catch (error) {
            console.error('Error fetching opportunities:', error);
            return createError('An unexpected error occurred', 'UNEXPECTED_ERROR', { error });
        }
    }

    async createOpportunity(data) {
        try {
            if (!data) return createError('Opportunity data is required', 'INVALID_INPUT');

            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return createError('User not authenticated', 'UNAUTHORIZED');

            // Fetch user's team_id
            const { data: userProfile } = await supabase
                .from('users')
                .select('team_id')
                .eq('id', user.id)
                .single();

            if (!userProfile?.team_id) console.warn('User has no team_id');

            // Pre-process amount to number if it's a string
            if (data.amount && typeof data.amount === 'string') {
                data.amount = parseFloat(data.amount);
            }
            if (data.probability && typeof data.probability === 'string') {
                data.probability = parseInt(data.probability);
            }
            if (data.close_date === '') {
                data.close_date = null;
            }

            const result = opportunitySchema.safeParse(data);

            if (!result.success) {
                const errorMessage = result.error.errors[0]?.message || 'Validation failed';
                return createError(errorMessage, 'VALIDATION_ERROR', { errors: result.error.errors });
            }

            const validData = result.data;

            // Populate defaults if missing
            if (!validData.owner_user_id) validData.owner_user_id = user.id;
            if (userProfile?.team_id && !validData.team_id) validData.team_id = userProfile.team_id;

            const { data: newOpportunity, error } = await supabase
                .from(this.tableName)
                .insert(validData)
                .select()
                .single();

            if (error) return parseSupabaseError(error, 'create opportunity');

            return createSuccess(newOpportunity, 'Opportunity created successfully');

        } catch (error) {
            console.error('Error creating opportunity:', error);
            return createError('An unexpected error occurred', 'UNEXPECTED_ERROR', { error });
        }
    }

    async updateOpportunity(id, data) {
        try {
            if (!id) return createError('ID is required', 'INVALID_INPUT');

            const partialSchema = opportunitySchema.partial();
            const result = partialSchema.safeParse(data);

            if (!result.success) {
                const errorMessage = result.error.errors[0]?.message || 'Validation failed';
                return createError(errorMessage, 'VALIDATION_ERROR', { errors: result.error.errors });
            }

            const { data: updatedOpp, error } = await supabase
                .from(this.tableName)
                .update(result.data)
                .eq('id', id)
                .select()
                .single();

            if (error) return parseSupabaseError(error, 'update opportunity');

            return createSuccess(updatedOpp, 'Opportunity updated successfully');

        } catch (error) {
            console.error('Error updating opportunity:', error);
            return createError('An unexpected error occurred', 'UNEXPECTED_ERROR', { error });
        }
    }

    async deleteOpportunity(id) {
        try {
            if (!id) return createError('Opportunity ID is required', 'INVALID_INPUT');

            const { error } = await supabase
                .from(this.tableName)
                .delete()
                .eq('id', id);

            if (error) return parseSupabaseError(error, 'delete opportunity');

            return createSuccess(null, 'Opportunity deleted successfully');
        } catch (error) {
            console.error('Error deleting opportunity:', error);
            return createError('An unexpected error occurred', 'UNEXPECTED_ERROR', { error });
        }
    }

    /**
     * Fetches history for a specific opportunity
     */
    async getHistory(opportunityId) {
        try {
            if (!opportunityId) return createError('Opportunity ID is required', 'INVALID_INPUT');

            const { data, error } = await supabase
                .from(TABLES.OPPORTUNITY_HISTORY)
                .select(`
          *,
          users ( full_name )
        `)
                .eq('opportunity_id', opportunityId)
                .order('changed_at', { ascending: false }); // Newest first

            if (error) return parseSupabaseError(error, 'fetch history');

            return createSuccess(data || []);
        } catch (error) {
            console.error('Error fetching history:', error);
            return createError('An unexpected error occurred', 'UNEXPECTED_ERROR', { error });
        }
    }

    /**
     * Adds a new history entry (system or user note)
     */
    async addHistoryEntry(data) {
        try {
            if (!data.opportunity_id) return createError('Opportunity ID is required', 'INVALID_INPUT');

            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return createError('User not authenticated', 'UNAUTHORIZED');

            const entry = {
                ...data,
                changed_by_user_id: user.id,
                changed_at: new Date().toISOString()
            };

            const { data: result, error } = await supabase
                .from(TABLES.OPPORTUNITY_HISTORY)
                .insert(entry)
                .select()
                .single();

            if (error) return parseSupabaseError(error, 'add history entry');

            return createSuccess(result);
        } catch (error) {
            console.error('Error adding history:', error);
            return createError('An unexpected error occurred', 'UNEXPECTED_ERROR', { error });
        }
    }
}

export const opportunitiesService = new OpportunitiesService();
